/* Períodos contables del estudio.
   ─────────────────────────────────────────────────────────────────────────
   Un estudio no siempre cierra el mes el día 1. Es habitual el corte "del 21
   al 20": el mes de agosto va del 21 de julio al 20 de agosto, y todo lo que
   se cobre o se gaste el 21 de agosto ya es septiembre.

   `diaCorte` es el ÚLTIMO día que entra en el período (el 20 cuenta como
   agosto) y vive en los ajustes de CADA estudio, así que cambiarlo en uno no
   toca a los demás. Con diaCorte = 1 el período es el mes calendario de
   siempre, que es como sigue funcionando cualquier estudio que no lo cambie.

   Todo se calcula en la hora local del navegador a propósito: el cierre es una
   fecha del estudio, no un instante UTC. */

export const DIA_CORTE_DEFAULT = 1;
/** Febrero no tiene 29, 30 ni 31: un corte más allá del 28 no existiría todos
 *  los meses. */
export const DIA_CORTE_MAX = 28;

export type Rango = { desde: Date; hasta: Date };

/** Qué período está mirando el usuario. Solo dos formas de elegir: el mes
 *  contable del estudio, o fechas exactas escritas a mano. */
export type SeleccionPeriodo =
  | { tipo: 'periodo'; anio: number; mes: number }   // un mes contable concreto
  | { tipo: 'rango'; desde: string; hasta: string }; // fechas exactas, YYYY-MM-DD

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const MESES_CORTOS = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

export const nombreMes = (mes: number) => MESES[((mes % 12) + 12) % 12];

export function normalizarDiaCorte(v: any): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return DIA_CORTE_DEFAULT;
  return Math.min(DIA_CORTE_MAX, Math.max(1, Math.round(n)));
}

/** Convierte a Date lo que guarda el sistema: 'YYYY-MM-DD', ISO completo,
 *  Date o timestamp. Las fechas sin hora se leen como hora local — leerlas
 *  como UTC correría el día hacia atrás en Chile y un pago del día 20 caería
 *  en el período equivocado justo en el borde del cierre. */
export function aFecha(valor: any): Date | null {
  if (valor == null || valor === '') return null;
  if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
  if (typeof valor === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor.trim());
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  }
  const d = new Date(valor);
  return isNaN(d.getTime()) ? null : d;
}

/** 'YYYY-MM-DD' de una fecha, en hora local. */
export function aISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Instante exacto en que cierra el período (anio, mes). El día de corte entra
 *  completo, así que el cierre es el final de ese día. */
function cierre(anio: number, mes: number, diaCorte: number): Date {
  if (diaCorte <= 1) return new Date(anio, mes + 1, 0, 23, 59, 59, 999); // último día del mes
  const ultimoDelMes = new Date(anio, mes + 1, 0).getDate();
  return new Date(anio, mes, Math.min(diaCorte, ultimoDelMes), 23, 59, 59, 999);
}

/** Rango del período contable que cierra en (anio, mes). El inicio se calcula
 *  como "un milisegundo después del cierre anterior", que es lo único que no
 *  se rompe en meses de 28, 30 y 31 días. */
export function rangoPeriodo(anio: number, mes: number, diaCorte: number): Rango {
  const d = normalizarDiaCorte(diaCorte);
  const hasta = cierre(anio, mes, d);
  const anterior = cierre(anio, mes - 1, d); // Date resuelve solo mes = -1
  return { desde: new Date(anterior.getTime() + 1), hasta };
}

/** A qué período contable pertenece una fecha. */
export function periodoDeFecha(valor: any, diaCorte: number): { anio: number; mes: number } | null {
  const f = aFecha(valor);
  if (!f) return null;
  const d = normalizarDiaCorte(diaCorte);
  if (d <= 1) return { anio: f.getFullYear(), mes: f.getMonth() };
  // Si ya pasó el cierre de su propio mes, la fecha pertenece al siguiente.
  const cierreDelMes = cierre(f.getFullYear(), f.getMonth(), d);
  if (f.getTime() > cierreDelMes.getTime()) {
    const sig = new Date(f.getFullYear(), f.getMonth() + 1, 1);
    return { anio: sig.getFullYear(), mes: sig.getMonth() };
  }
  return { anio: f.getFullYear(), mes: f.getMonth() };
}

/** Rango de fechas exactas escritas a mano. Se toma el día completo en ambos
 *  extremos, que es lo que espera quien escribe "del 3 al 17". */
export function rangoExacto(desde: string, hasta: string): Rango {
  const d = aFecha(desde) ?? new Date(1970, 0, 1);
  const h = aFecha(hasta) ?? new Date();
  const finDia = new Date(h.getFullYear(), h.getMonth(), h.getDate(), 23, 59, 59, 999);
  // Si vienen al revés, se ordenan en vez de devolver un rango vacío.
  return d.getTime() <= finDia.getTime()
    ? { desde: d, hasta: finDia }
    : { desde: new Date(h.getFullYear(), h.getMonth(), h.getDate(), 0, 0, 0, 0), hasta: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999) };
}

export function rangoDeSeleccion(sel: SeleccionPeriodo, diaCorte: number, _hoy = new Date()): Rango {
  return sel.tipo === 'periodo'
    ? rangoPeriodo(sel.anio, sel.mes, diaCorte)
    : rangoExacto(sel.desde, sel.hasta);
}

/** "21 jul 2026 – 20 ago 2026". El año se repite solo si cambia. */
export function etiquetaRango(r: Rango): string {
  const f = (d: Date) => `${d.getDate()} ${MESES_CORTOS[d.getMonth()]}`;
  const mismoAnio = r.desde.getFullYear() === r.hasta.getFullYear();
  return mismoAnio
    ? `${f(r.desde)} – ${f(r.hasta)} ${r.hasta.getFullYear()}`
    : `${f(r.desde)} ${r.desde.getFullYear()} – ${f(r.hasta)} ${r.hasta.getFullYear()}`;
}

/** Nombre corto de lo seleccionado: "Agosto 2026" o "Rango exacto". */
export function etiquetaSeleccion(sel: SeleccionPeriodo): string {
  return sel.tipo === 'periodo' ? `${nombreMes(sel.mes)} ${sel.anio}` : 'Rango exacto';
}

export function enRango(valor: any, r: Rango): boolean {
  const f = aFecha(valor);
  if (!f) return false;
  return f.getTime() >= r.desde.getTime() && f.getTime() <= r.hasta.getTime();
}

/** Hasta el cierre del período, sin límite por abajo. Es lo que corresponde
 *  para saldos acumulados: cuánta plata hay en la cuenta a esa fecha. */
export function hastaCierre(valor: any, r: Rango): boolean {
  const f = aFecha(valor);
  if (!f) return false;
  return f.getTime() <= r.hasta.getTime();
}

/** Explica el corte en una línea, con el período en curso como ejemplo. */
export function explicacionCorte(diaCorte: number, hoy = new Date()): string {
  const d = normalizarDiaCorte(diaCorte);
  if (d <= 1) return 'El mes contable es el mes calendario: del 1 al último día.';
  const p = periodoDeFecha(hoy, d)!;
  const r = rangoPeriodo(p.anio, p.mes, d);
  return `El mes contable cierra el día ${d}. Hoy corre ${nombreMes(p.mes).toLowerCase()} ${p.anio}: ${etiquetaRango(r)}.`;
}
